import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';

import { makeNamespacedLog } from '@sb/log';
import { DesireMode } from '@sb/messenger/auto_generated/messenger/arm_control_bot_service';
import { EventEmitter } from '@sb/utilities';
import type { AssemblyRunnableState } from '@sbrc/services/assembly-client';
import {
  AssemblyExecutionState,
  getAssemblyServerHandle,
} from '@sbrc/services/assembly-client';
import { useRobotIdentity } from '@sbrc/services/feathers-client/robot-identity';
import { getService } from '@sbrc/services/feathers-client/utils';

const log = makeNamespacedLog('useDesireMode');

export interface UseDesireModeResult {
  desireMode: DesireMode | null;
  setDesireMode: (mode: DesireMode) => Promise<{ success: boolean }>;
}

const STATIC_LIVE_RESULT: UseDesireModeResult = {
  desireMode: DesireMode.DESIRE_MODE_LIVE,
  // eslint-disable-next-line @typescript-eslint/require-await
  setDesireMode: async () => ({ success: true }),
};

// ── Shared module-level state ──────────────────────────────────────────
// All useDesireMode() hook instances share this state so that a mode
// change in one component (e.g. DesireModeSelector in the AppBar)
// is immediately visible in another (e.g. main.tsx route dispatch).

let sharedDesireMode: DesireMode | null = null;
let sharedPreviousMode: DesireMode | null = null;
let pendingChange = false;
let pendingChangeTimeoutId: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
// Set to true whenever a LIVE patch event is suppressed during runnable execution.
// Cleared when execution ends and a resync check is scheduled.
let suppressedLiveEvent = false;
// Tracks whether a runnable is currently active, updated by the runnableState
// subscription. Note: this flag has the same event-ordering limitation as
// getState() — if a desireMode `patched(LIVE)` event arrives before the
// corresponding `runnableState(running)` event, isRunnableRunning will still
// be false and the patch will not be suppressed. The practical impact is a
// brief UX glitch (mode selector shows LIVE momentarily) rather than a
// functional bug, because the selector is disabled during execution.
let isRunnableRunning = false;

// Cap how long pendingChange can suppress external `patched` events. Without
// this, a hung patch would silently mute future broadcasts forever.
const PENDING_CHANGE_TIMEOUT_MS = 10_000;

const emitter = new EventEmitter<{ change: DesireMode | null }>();

function setSharedDesireMode(mode: DesireMode | null) {
  sharedDesireMode = mode;
  emitter.emit('change', mode);
}

function clearPendingChange() {
  pendingChange = false;

  if (pendingChangeTimeoutId !== null) {
    clearTimeout(pendingChangeTimeoutId);
    pendingChangeTimeoutId = null;
  }
}

function armPendingChange() {
  clearPendingChange();
  pendingChange = true;

  pendingChangeTimeoutId = setTimeout(
    clearPendingChange,
    PENDING_CHANGE_TIMEOUT_MS,
  );
}

/**
 * Returns the robby-server SET_CONFIG payload for a given desire mode.
 * robby-server owns the full transition (its desire loop, the firmware
 * assembly-mode flag, and the ACB desire-mode patch, in a safe order — see
 * AssemblyManager::transitionControlMode); the app only states the target.
 */
function robbyStateForMode(mode: DesireMode | null): {
  desireLoopEnabled: boolean;
  controlMode: string;
} {
  switch (mode) {
    case DesireMode.DESIRE_MODE_LIVE:
      return { desireLoopEnabled: false, controlMode: 'live' };
    case DesireMode.DESIRE_MODE_ASSEMBLY:
      return { desireLoopEnabled: true, controlMode: 'assembly' };
    default:
      return { desireLoopEnabled: false, controlMode: 'idle' };
  }
}

/**
 * Polls until the transition actually completed: ACB reports the expected mode
 * AND robby-server reports the expected desire-loop state. robby runs the
 * transition asynchronously and can abort its half (e.g. fail-closed when it
 * can't confirm ACB is off the bus) while ACB's mode already reads the target —
 * so verifying ACB alone could report success when robby never took the bus.
 */
async function verifyTransition(
  expectedAcbMode: DesireMode,
  expectedDesireLoopEnabled: boolean,
): Promise<boolean> {
  const service = getService('desireMode')();
  const assemblyConnection = getAssemblyServerHandle();

  // robby-server's transition includes an ACB desire-mode PATCH with a 5s HTTP
  // timeout (AssemblyManager::patchAcbDesireMode), so a slow-but-successful
  // handoff can take >5s. Poll well past that so we don't falsely time out and
  // fire a rollback SET_CONFIG that fights the still-in-flight transition.
  const POLL_INTERVAL_MS = 300;
  const MAX_WAIT_MS = 10_000;
  const maxAttempts = Math.ceil(MAX_WAIT_MS / POLL_INTERVAL_MS);

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await service.find({});

      // desireMode.find returns a single object; guard against an unexpected
      // paginated/array shape so a mismatch fails loudly here rather than
      // silently polling to timeout (which would trigger a rollback).
      const currentMode =
        result != null && !Array.isArray(result)
          ? (result as { currentMode?: number }).currentMode
          : undefined;

      // robby publishes desireLoopEnabled via WS state patches — confirms robby
      // actually took/relinquished the bus, not just that ACB's mode matches.
      const desireLoopEnabled =
        assemblyConnection.getState('desireLoopEnabled');

      if (
        currentMode === expectedAcbMode &&
        desireLoopEnabled === expectedDesireLoopEnabled
      ) {
        return true;
      }
    } catch {
      // feathers/ACB transiently unavailable — keep polling
    }

    // Check first, then sleep — so an already-complete handoff returns without
    // paying a poll-interval floor.
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, POLL_INTERVAL_MS);
    });
  }

  return false;
}

/**
 * Global hook for reading and setting the ACB desire mode.
 *
 * In non-assembly operation modes, short-circuits with a static LIVE value
 * (no Feathers fetch, no subscription, no error path).
 *
 * Sources of truth:
 * - Initial state: ACB GetDesireMode RPC (LIVE vs non-LIVE), defaults to ASSEMBLY
 * - User changes: optimistic shared state
 * - External LIVE transitions: Feathers patched events from ACB
 */
export function useDesireMode(): UseDesireModeResult {
  const robotIdentity = useRobotIdentity();

  const isAssembly =
    robotIdentity?.robotOperationMode === 'assembly' ||
    robotIdentity?.robotOperationMode === 'assembly_simulated';

  const isCalibration = robotIdentity?.robotOperationMode === 'calibration';

  // Calibration stations run no arm-control-bot: there is no ACB to fetch or
  // subscribe to, so the current submode derives from robby-server's WS state
  // instead — desire loop streaming → Assembly, stopped → Idle.
  // useSyncExternalStore reads the cached snapshot during render, so the
  // first calibration render already reflects an already-known Idle state
  // instead of flashing Assembly for a frame (identity resolves async, so a
  // once-only useState initializer would run before isCalibration is known).
  // Both closures are gated on isCalibration: getAssemblyServerHandle()
  // opens the robby-server WebSocket on first use, which must not happen on
  // live robots (no robby-server there).
  const calibrationLoopEnabled = useSyncExternalStore(
    useCallback(
      (onStoreChange: () => void) =>
        isCalibration
          ? getAssemblyServerHandle().listenForStateUpdates(
              'desireLoopEnabled',
              onStoreChange,
            )
          : () => {},
      [isCalibration],
    ),
    () =>
      isCalibration
        ? getAssemblyServerHandle().getState('desireLoopEnabled')
        : undefined,
  );

  const setCalibrationDesireMode = useCallback(
    async (mode: DesireMode): Promise<{ success: boolean }> => {
      // No ACB on this station: Live is not a reachable submode. The selector
      // disables it; reject defensively for any other caller. Log it — a
      // silent no-op here is invisible to anyone who bypasses the selector.
      if (mode === DesireMode.DESIRE_MODE_LIVE) {
        log.warn(
          `uncategorized`,
          'Refusing to set Live desire mode: this station runs no arm-control-bot',
        );

        return { success: false };
      }

      const assemblyConnection = getAssemblyServerHandle();
      const target = robbyStateForMode(mode);
      const sendError = assemblyConnection.sendUpdate(target);

      if (sendError) {
        return { success: false };
      }

      // Verify against robby-server state only — there is no ACB mode to
      // poll. Check before sleeping so an already-applied transition returns
      // without paying a poll-interval floor.
      const POLL_INTERVAL_MS = 300;
      const MAX_WAIT_MS = 10_000;
      const maxAttempts = Math.ceil(MAX_WAIT_MS / POLL_INTERVAL_MS);

      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (
          assemblyConnection.getState('desireLoopEnabled') ===
          target.desireLoopEnabled
        ) {
          return { success: true };
        }

        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => {
          setTimeout(resolve, POLL_INTERVAL_MS);
        });
      }

      return { success: false };
    },
    [],
  );

  const [desireMode, setLocalState] = useState<DesireMode | null>(
    sharedDesireMode,
  );

  // Subscribe to shared state changes
  useEffect(() => {
    const unsubscribe = emitter.on('change', setLocalState);

    // Sync in case shared state changed between render and effect
    if (sharedDesireMode !== desireMode) {
      setLocalState(sharedDesireMode);
    }

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset module-level flags when leaving assembly mode so re-entering
  // triggers a fresh fetch and stale event state doesn't bleed into the next session.
  useEffect(() => {
    if (!isAssembly) {
      initialized = false;
      suppressedLiveEvent = false;
      isRunnableRunning = false;
    }
  }, [isAssembly]);

  // Fetch initial desire mode from ACB (once per assembly session)
  useEffect(() => {
    if (!isAssembly || initialized) return;
    initialized = true;

    // Read-only: reflect the current mode into the UI. This effect must NOT
    // write to robby-server. It used to push {desireLoopEnabled: true,
    // controlMode: 'assembly'} whenever ACB reported non-LIVE — so a stale
    // read racing an in-flight switch to Live would re-engage the assembly
    // desire loop while ACB was live-streaming, wedging the box in a firmware
    // rejection/violation storm until reboot. Ownership of the assembly desire
    // loop lives server-side now (robby-server's startup reconcile and
    // SET_CONFIG transitions — see AssemblyManager::transitionControlMode).
    const fetchDesireMode = async () => {
      try {
        const service = getService('desireMode')();
        const result = (await service.find({})) as { currentMode?: number };

        if (result.currentMode === DesireMode.DESIRE_MODE_LIVE) {
          setSharedDesireMode(DesireMode.DESIRE_MODE_LIVE);
        } else {
          // ACB returns IDLE for both ASSEMBLY and IDLE. Display ASSEMBLY,
          // the standard state for assembly operation mode.
          setSharedDesireMode(DesireMode.DESIRE_MODE_ASSEMBLY);
        }
      } catch {
        // ACB might not support desire mode yet (e.g. assembly_simulated has
        // no ACB); display ASSEMBLY.
        setSharedDesireMode(DesireMode.DESIRE_MODE_ASSEMBLY);
      }
    };

    fetchDesireMode();
  }, [isAssembly]);

  // Subscribe to Feathers patched events for external mode changes
  // (e.g. another tab or robby-server switching modes)
  useEffect(() => {
    if (!isAssembly) return undefined;

    const service = getService('desireMode')();

    const onPatched = (data: { currentMode?: number }) => {
      if (pendingChange) {
        clearPendingChange();

        return;
      }

      // Ignore external LIVE transitions while a runnable is actively executing.
      // The runnable manages ACB mode internally and restores it on completion;
      // surfacing the intermediate LIVE state would disrupt the assembly tool view.
      // Use the subscription-maintained isRunnableRunning flag rather than
      // getState() to avoid reading a stale snapshot when Feathers patched and
      // runnableState updates arrive out of order.
      if (data.currentMode === DesireMode.DESIRE_MODE_LIVE) {
        if (isRunnableRunning) {
          suppressedLiveEvent = true;

          return;
        }

        setSharedDesireMode(DesireMode.DESIRE_MODE_LIVE);
      } else if (sharedDesireMode === DesireMode.DESIRE_MODE_LIVE) {
        // External transition away from LIVE (e.g. another client switched
        // to Assembly/Idle). Default to ASSEMBLY since ACB can't distinguish
        // ASSEMBLY from IDLE in its response.
        setSharedDesireMode(DesireMode.DESIRE_MODE_ASSEMBLY);
      }
    };

    service.on('patched', onPatched);

    return () => {
      service.removeListener('patched', onPatched);
    };
  }, [isAssembly]);

  // When a runnable completes, re-check the actual desire mode in case a LIVE
  // patch event was suppressed during execution and ACB stayed in LIVE (e.g. an
  // external client overrode the runnable's enforceAssemblyMode_(true) call).
  // The delay gives the runnable's mode-restore time to propagate as a Feathers
  // patch event before we query — so we only update if ACB genuinely stayed LIVE.
  useEffect(() => {
    if (!isAssembly) return undefined;

    const assemblyConnection = getAssemblyServerHandle();

    const isAnyRunnableRunning = (
      states: { [key: string]: AssemblyRunnableState } | undefined,
    ) =>
      Object.values(states ?? {}).some(
        (s) => s?.executionState === AssemblyExecutionState.Running,
      );

    let prevWasRunning = isAnyRunnableRunning(
      assemblyConnection.getState('runnableStates'),
    );

    isRunnableRunning = prevWasRunning;

    let pendingResyncTimeout: ReturnType<typeof setTimeout> | null = null;

    const unsubscribe = assemblyConnection.listenForStateUpdates(
      'runnableStates',
      (states) => {
        const isRunning = isAnyRunnableRunning(states);
        const wasRunning = prevWasRunning;
        isRunnableRunning = isRunning;
        prevWasRunning = isRunning;

        if (wasRunning && !isRunning && suppressedLiveEvent) {
          suppressedLiveEvent = false;

          if (pendingResyncTimeout !== null) {
            clearTimeout(pendingResyncTimeout);
          }

          pendingResyncTimeout = setTimeout(() => {
            pendingResyncTimeout = null;

            // A new run may have started during the 2s delay — don't flip to
            // LIVE mid-execution.
            if (
              isAnyRunnableRunning(
                assemblyConnection.getState('runnableStates'),
              )
            ) {
              return;
            }

            const desireModeService = getService('desireMode')();

            (desireModeService.find({}) as Promise<{ currentMode?: number }>)
              .then((result) => {
                // Re-check execution state — a new run may have started while
                // the find() was in flight.
                if (
                  isAnyRunnableRunning(
                    assemblyConnection.getState('runnableStates'),
                  )
                ) {
                  return;
                }

                if (
                  result?.currentMode === DesireMode.DESIRE_MODE_LIVE &&
                  sharedDesireMode !== DesireMode.DESIRE_MODE_LIVE
                ) {
                  setSharedDesireMode(DesireMode.DESIRE_MODE_LIVE);
                }
              })
              .catch(() => {});
            // 2 s gives robby-server's enforceAssemblyMode_(true) PATCH time to reach
            // feathers-bot and propagate back as a Feathers patched event before we query.
            // Measured ACB propagation round-trip is typically 200–500 ms; 2 s is a
            // conservative upper bound. An event-driven approach would be more robust.
          }, 2000);
        }
      },
    );

    return () => {
      if (pendingResyncTimeout !== null) {
        clearTimeout(pendingResyncTimeout);
        pendingResyncTimeout = null;
      }

      suppressedLiveEvent = false;
      isRunnableRunning = false;
      unsubscribe();
    };
  }, [isAssembly]);

  const setDesireMode = useCallback(
    async (mode: DesireMode): Promise<{ success: boolean }> => {
      sharedPreviousMode = sharedDesireMode;
      armPendingChange();
      setSharedDesireMode(mode);

      try {
        // One writer: send the target mode to robby-server, which sequences
        // the entire handoff (stop/start its desire loop, flip firmware
        // assembly mode, patch ACB) so no intermediate state ever has both
        // controllers streaming. The previous client-side sequencing patched
        // ACB in parallel with robby's 100ms drain — every legitimate switch
        // to Live bounced its first desires off assembly-mode firmware and
        // self-inflicted a violation + failure state.
        const assemblyConnection = getAssemblyServerHandle();

        // sendUpdate returns a non-null error string when the WebSocket is
        // disconnected — robby never received SET_CONFIG, so fail fast instead
        // of polling for 10s and possibly reporting success off a stale ACB mode.
        const sendError = assemblyConnection.sendUpdate(
          robbyStateForMode(mode),
        );

        if (sendError) {
          throw new Error(
            `Failed to send mode change to robby-server: ${sendError}`,
          );
        }

        const { desireLoopEnabled, controlMode } = robbyStateForMode(mode);

        const expectedAcbMode =
          controlMode === 'live'
            ? DesireMode.DESIRE_MODE_LIVE
            : DesireMode.DESIRE_MODE_IDLE; // ACB collapses ASSEMBLY/IDLE to IDLE

        if (!(await verifyTransition(expectedAcbMode, desireLoopEnabled))) {
          throw new Error('Mode transition did not complete');
        }

        return { success: true };
      } catch {
        // Rollback UI state and restore robby-server desireLoopEnabled
        clearPendingChange();
        setSharedDesireMode(sharedPreviousMode);

        const assemblyConnection = getAssemblyServerHandle();

        assemblyConnection.sendUpdate(robbyStateForMode(sharedPreviousMode));

        return { success: false };
      }
    },
    [],
  );

  // Short-circuit for non-assembly operation modes. Calibration is
  // assembly-like but has no arm-control-bot: robby-server owns desires, so
  // the submode derives from its desire-loop state and transitions go through
  // its SET_CONFIG (Live is unreachable). Every other non-assembly mode is a
  // live robot.
  if (!isAssembly) {
    if (isCalibration) {
      return {
        desireMode:
          calibrationLoopEnabled === false
            ? DesireMode.DESIRE_MODE_IDLE
            : DesireMode.DESIRE_MODE_ASSEMBLY,
        setDesireMode: setCalibrationDesireMode,
      };
    }

    return STATIC_LIVE_RESULT;
  }

  return { desireMode, setDesireMode };
}
